import React, { useEffect, useRef, useState } from 'react';
import { X as XIcon } from 'lucide-react';
import { useI18n } from '../../../i18n/I18nProvider';
import { getNotifConfig } from '../notificationIcons';
import type { NotificationAction, NotificationItem } from '../api';
import './LiveNotificationToast.css';

/** Worth interrupting for: something happened to you, or something waits on you. */
export const LIVE_TYPES = new Set([
  'kudos_received', 'badge_earned', 'level_up', 'certificate_issued',
  'points_approval_needed', 'new_event', 'event_shared', 'event_waitlist_promoted',
  'speaker_request_reviewed', 'speaker_invited', 'speaker_invitation_answered', 'event_owner_assigned',
  'mandatory_enrolled', 'mandatory_reminder',
  'mandatory_reminder_7d', 'mandatory_reminder_1d',
]);

const VISIBLE_MS = 8000;

interface Props {
  item: NotificationItem | null;
  onAction: (item: NotificationItem, action: NotificationAction) => void;
  onOpen: (item: NotificationItem) => void;
  onClose: () => void;
}

/**
 * The bottom-left pop-up for a notification that just arrived, with its main action. Polite
 * (announced when the reader is idle, never stealing focus), gone after eight seconds unless
 * the pointer is on it, and still: the entrance motion is off for anyone who asked for reduced
 * motion.
 */
export default function LiveNotificationToast({ item, onAction, onOpen, onClose }: Props) {
  const { t } = useI18n();
  const [hovered, setHovered] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!item || hovered) return undefined;
    timer.current = setTimeout(onClose, VISIBLE_MS);
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [item, hovered, onClose]);

  const primary = item?.actions?.find((a) => a.primary);
  const Icon = item ? getNotifConfig(item.type).Icon : null;

  return (
    <div className="live-toast__region" role="status" aria-live="polite">
      {item && Icon && (
        <div
          className="live-toast ui-inverse"
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          <span className="live-toast__icon" aria-hidden="true"><Icon size={18} /></span>
          <button type="button" className="live-toast__text" onClick={() => { onOpen(item); onClose(); }}>
            <span className="live-toast__title">{item.title}</span>
            {item.message && <span className="live-toast__message">{item.message}</span>}
          </button>
          {primary && (
            <button
              type="button"
              className="live-toast__action"
              onClick={() => { onAction(item, primary); onClose(); }}
            >
              {t(`notifications.actions.${primary.key}`)}
            </button>
          )}
          <button type="button" className="live-toast__close" onClick={onClose} aria-label={t('notifications.dismiss')}>
            <XIcon size={14} />
          </button>
        </div>
      )}
    </div>
  );
}
